import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { css, useTheme } from "@emotion/react";
import { cloneDeep, isEqual, isEmpty, uniq } from "lodash";

import { useEventRegistrationContext } from "core/contexts/EventRegistrationContext";
import useBreakpoint from "core/hooks/useBreakpoint";
import useEventRegistrationForm from "core/hooks/useEventRegistrationForm";
import { OptionalOnFieldChangeArgs } from "core/hooks/useForm";
import RoundButton from "core/Components/Buttons/RoundButton";
import ConfirmationRemoveAlertDialog from "pages/Components/CartAndCheckout/components/ConfirmationRemoveAlertDialog";
import Collapse from "core/Components/Collapse";
import ContentCombine from "core/Components/ContentCombine";
import Dialog from "core/Components/Dialog/Dialog";
import DialogContent from "core/Components/Dialog/DialogContent";
import EventFormContainer from "pages/Components/EventRegistration/EventFormContainer";
import EventPageContentContainer from "pages/Components/EventRegistration/EventsPageContentContainer";
import EventRegistrationFormActions from "pages/Components/EventRegistration/EventRegistrationFormActions";
import Form from "core/Components/Form/Form";
import ErrorIcon from "core/Components/Form/ErrorIcon";
import FormButton from "core/Components/Form/FormButton";
import RegistrationForm from "pages/Components/RegistrationForm";
import RouteLeavingGuard from "core/Components/RouteLeavingGuard";
import { isDown, isUp } from "theme/breakpoints";
import window from "core/includes/window";

import { ReactComponent as Minus } from 'images/icons/minus-icon.svg';
import { ReactComponent as Plus } from 'images/icons/plus-icon.svg';
import { ReactComponent as Chevron } from "images/icons/thick-chevron.svg";

type Props = {
    eventPage: EventPage,
    registrantsData: Array<RegistrantData>,
    userCode: string|null,
};

const EventRegistrationPage: React.FC<Props> = ({
    eventPage,
    registrantsData,
    userCode,
}) => {
    const registrationPageData: EventRegistrationPage = eventPage.registrationPage;
    const theme = useTheme();
    const { breakpoint } = useBreakpoint();
    const {
        expandedIds,
        setExpandedIds,
        directStateSetExpandedIds,
        toggleExpandedId,
        getIsIdExpanded,
        getIsIdExpandedDelayed,
        selectedRegistrantIndex,
        setSelectedRegistrantIndex,
        isEditing,
    } = useEventRegistrationContext();

    const formFields = registrationPageData?.formFields;
    const allFormFields = registrationPageData?.allFormFields;
    const initialFields: any = { additionalRegistrants: {} };
    const [registrantIndexQueuedToRemove, setRegistrantIndexQueuedToRemove] = useState<number|null>(null);
    const [registrantsWithErrorIndices, setRegistrantsWithErrorIndices] = useState<any>([]);
    const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

    const doNotValidateNewRegistrant = useRef(true);
    const ignoreFormFieldCallback = useCallback((f: FormField) => f.isOnConfirmationPage, []);
    const disableOverflow = useRef(true);
    const containerRefs: any = useRef({});

    // Make sure that when you navigate back to edit, there isn't any weirdness with the overflow on first load.
    useEffect(() => {
        setTimeout(() => {
            disableOverflow.current = false;
        }, 500);
    }, []);

    const {
        registrationFields: fields,
        registrationErrors: errors,
        formFieldsForRender,
        getFormFieldsForRender,
        onFieldChange,
        onSubmit,
        isValid,
        isFormChanged,
        isSubmitting,
    } = useEventRegistrationForm({
        fields: initialFields,
        formFields,
        allFormFields,
        registrationPageData,
        userCode,
        initialRegistrantsData: registrantsData,
        ignoreFormFieldCallback,
        confirmTo: 'detail-confirmation',
    });

    useEffect(() => {
        const beforeUnload = (e: any) => {
            // Cancel the event
            e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
            // Chrome requires returnValue to be set
            e.returnValue = '';
        };

        window.addEventListener("beforeunload", beforeUnload);

        return () => window.removeEventListener("beforeunload", beforeUnload);
    }, []);

    const firstNameId = formFields.find((f: FormField) => f.code === 'firstname')?.id;
    const lastNameId = formFields.find((f: FormField) => f.code === 'lastname')?.id;

    const totalNumberOfRegistrants = 1 + Object.keys(fields.additionalRegistrants).length;

    const collapseIdPrefix = `registration-${eventPage.id}-registrant-`;

    const getCollapseId = useCallback((registrantIndex: string|number) => (
        `registration-${eventPage.id}-registrant-${registrantIndex}`
    ), [eventPage]);

    const getRegistrantName = useCallback((fields: any) => {
        let name = null;
        if (firstNameId && fields[firstNameId]) {
            name = fields[firstNameId];

            if (lastNameId && fields[lastNameId]) name += ` ${fields[lastNameId]}`;
        }

        return name;
    }, [firstNameId, lastNameId]);

    const allRegistrantNames: any = useMemo(() => {
        let allRegistrantNames: any = { 0: null };

        const firstRegistrantName = getRegistrantName(fields);

        if (firstRegistrantName) allRegistrantNames[0] = firstRegistrantName;

        Object.entries(fields.additionalRegistrants).forEach(([index, registrantFields]) => {
            allRegistrantNames[parseInt(index) + 1] = getRegistrantName(registrantFields);
        });

        return allRegistrantNames;
    }, [fields, getRegistrantName]);

    const onSubmitRegistration = useCallback(() => {
        setIsButtonLoading(true);
        doNotValidateNewRegistrant.current = false;

        onSubmit()
            .then(({ data }: { data: any }) => {
                const numberOfReg = data.submitEventRegistration.registrants.length;

                if (!isEditing) {
                    // Use timeout so they don't expand until next page.
                    setTimeout(() => {
                        setExpandedIds(Array.from(Array(numberOfReg || 0).keys()), collapseIdPrefix)
                    }, 500);
                }
            })
            .catch(() => setIsButtonLoading(false))
            .finally(() => {
                setIsButtonLoading(false);
            });
    }, [
        onSubmit, isEditing, collapseIdPrefix, setExpandedIds
    ]);

    const onChange = (name: string, value: any, index: number, opArgs?: OptionalOnFieldChangeArgs) => {
        onFieldChange(name, value, opArgs);
        setSelectedRegistrantIndex(index);
    }

    // The default form is expanded when it doesn't have any expanded IDs
    // for opening the Registration form the first time.
    const isCheckExpandedIdxFirstTime = useRef(false);
    useEffect(() => {
        if (!isCheckExpandedIdxFirstTime.current) {
            isCheckExpandedIdxFirstTime.current = true;
            if (isEmpty(expandedIds)) {
                setExpandedIds([0], collapseIdPrefix)
            }
        }
    }, [expandedIds, setExpandedIds, collapseIdPrefix]);

    const toggleExpanded = useCallback((index: number) => {
        const isExpanded = toggleExpandedId(
            index,
            collapseIdPrefix,
            { scrollRef: containerRefs.current[collapseIdPrefix + index] },
        );
        setSelectedRegistrantIndex((prev: number) => isExpanded ? index : prev);
    }, [toggleExpandedId, setSelectedRegistrantIndex, collapseIdPrefix]);

    const onClickName = useCallback((index: number) => {
        if (isUp(breakpoint, 'lg')) {
            setSelectedRegistrantIndex(index);
            setExpandedIds([index], collapseIdPrefix);
        } else {
            toggleExpanded(index);
        }
    }, [
        setSelectedRegistrantIndex, breakpoint, setExpandedIds, toggleExpanded, collapseIdPrefix,
    ]);

    const incrementAdditionalRegistrants = useCallback(() => {
        doNotValidateNewRegistrant.current = true;

        const newAddReg = fields.additionalRegistrants;

        newAddReg[Object.keys(newAddReg).length] = {};

        onFieldChange(
            'additionalRegistrants',
            newAddReg,
            { doNotValidate: true },
        );
        onClickName(Object.keys(newAddReg).length);
    }, [fields.additionalRegistrants, onFieldChange, onClickName]);

    const removeRegistrant = useCallback((index: number|null = null) => {
        let indexToRemove: number|null = null;

        if (index !== null) {
            indexToRemove = index;
        } else if (registrantIndexQueuedToRemove !== null) {
            indexToRemove = registrantIndexQueuedToRemove;
        }

        if (indexToRemove === null) return;

        const newAddReg = fields.additionalRegistrants;
        delete newAddReg[indexToRemove];

        Object.entries(newAddReg).forEach(([registrantIndex, registrant]) => {
            if (!!indexToRemove && parseInt(registrantIndex) < indexToRemove) return;

            delete newAddReg[registrantIndex];
            newAddReg[parseInt(registrantIndex) - 1] = registrant;

        });

        setSelectedRegistrantIndex((prev: number) => Math.min(prev, Object.keys(newAddReg).length));

        onFieldChange(
            'additionalRegistrants',
            newAddReg,
            { doNotValidate: true }
        );
        setRegistrantIndexQueuedToRemove(null);

        directStateSetExpandedIds((prev: Array<string>) => {
            const newExpanded = [...prev];
            const newExpandedWithMatchingIds = newExpanded.filter((id) => (
                id.indexOf(collapseIdPrefix) >= 0
            ));

            if (
                indexToRemove !== null &&
                (isEqual(newExpandedWithMatchingIds, [getCollapseId(indexToRemove + 1)]) || isEmpty(newExpandedWithMatchingIds))
            ) {
                newExpanded.push(getCollapseId(0));
                return uniq(newExpanded);
            } else {
                return prev;
            }
        })
    }, [
        fields, onFieldChange, registrantIndexQueuedToRemove, setSelectedRegistrantIndex,
        getCollapseId, collapseIdPrefix, directStateSetExpandedIds,
    ]);

    const onClickRemoveRegistrant = useCallback((indexInAddRegArray: number) => {
        const fieldsForRegistrant = fields.additionalRegistrants[indexInAddRegArray];

        if (isEmpty(fieldsForRegistrant)) {
            removeRegistrant(indexInAddRegArray);
        } else {
            setRegistrantIndexQueuedToRemove(indexInAddRegArray);
        }
    }, [fields, removeRegistrant]);

    const additionalRegistrantFormFields = formFields.filter((f:FormField) => f.isMandatoryForSecondaryRegistrant);

    useEffect(() => {
        let newRegistrantsWithErrorIndices = [];
        const clonedAdditionalRegistrantErrors = cloneDeep(errors.additionalRegistrants);
        const additionalRegistrantErrors = isEmpty(clonedAdditionalRegistrantErrors) ?
            {} : clonedAdditionalRegistrantErrors;
        const mainRegistrantErrors = cloneDeep(errors);

        delete mainRegistrantErrors.additionalRegistrants;

        if (!isEmpty(mainRegistrantErrors)) newRegistrantsWithErrorIndices.push(0);

        Object.keys(additionalRegistrantErrors).forEach(i => newRegistrantsWithErrorIndices.push(parseInt(i) + 1));

        setRegistrantsWithErrorIndices(newRegistrantsWithErrorIndices);
    }, [errors]);

    const isRouteLeavingGuardActive = !isSubmitting && isFormChanged;

    const FormComponent: any = isUp(breakpoint, 'lg') ? 'div' : Collapse;

    const leftContainerStyle = css`
        padding-top: 78px;

        ${theme.breakpoints.up('3xl')} {
            padding-top: 68px;
        }

        ${theme.breakpoints.only('lg')} {
            padding-top: 64px;
        }

        ${theme.breakpoints.down('md')} {
            display: none;
        }
    `;

    const registrationStyle = css`
        font-weight: ${theme.fonts.weights.bold};
    `;

    const inputTextStyle = css`
        margin-right: 12px;
        font-size: 14px;
        line-height: 17px;
    `;

    const eventRegistrationStyle = css`
        justify-content: flex-start;
        margin-bottom: 36px;

        ${theme.breakpoints.up('3xl')} {
            margin-bottom: 25px;
        }

        ${theme.breakpoints.only('lg')} {
            margin-bottom: 25px;
        }

        ${theme.breakpoints.down('md')} {
            justify-content: center;
        }

        ${theme.breakpoints.down('xs')} {
            margin-bottom: 36px;
        }
    `;

    const attendeesContainerStyle = css`
        width: 357px;
        padding: 18px 16px 10px;
        display: flex;
        flex-direction: column;
        background-color: ${theme.colours.grey[100]};
        border-radius: 17px;

        ${theme.breakpoints.only('lg')} {
            width: 313px;
            padding: 7px 8px 5px;
        }
    `;

    const attendeesNumberStyle = css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding-left: 24px;
        padding-right: 12px;
        padding-bottom: 10px;
        color: ${theme.colours.doveGray4};
        border-bottom: 1.5px solid ${theme.colours.silver6};

        ${theme.breakpoints.only('lg')} {
            padding-bottom: 7px;
        }
    `;

    const attendeeNameStyle = css`
        height: 48px;
        display: flex;
        flex-direction: row;
        flex-shrink: 0;
        justify-content: space-between;
        align-items: center;
        padding-left: 24px;
        border-bottom: 1.5px solid ${theme.colours.silver6};

        ${theme.breakpoints.only('lg')} {
            height: 42px;
        }
    `;

    const plusAndMinusIconContainerStyle = ({ isPlus = false, isForSmallBreakpoints = false } = {}) => css`
        ${theme.breakpoints.up('lg')} {
            ${isForSmallBreakpoints && css` display: none; `};
        }

        width: 34px;
        height: 34px;
        cursor: pointer;
        clip-path: circle(50%);
        background-color: transparent;

        ${theme.mixins.bloop};

        path {
            background-color: white;
        }

        &:hover {
            path  {
                fill: ${isPlus ? theme.colours.malibu4 : theme.colours.red[550]};
            }
        }

        ${theme.breakpoints.down('md')} {
            ${!isPlus && css`
                position: absolute;
                bottom: 12px;
                right: 19px;
                width: 33px;
                height: 33px;
            `};
        }
    `;

    const addNewPersonStyle = css`
        display: flex;
        padding-top: 10px;
        align-items: center;
        flex-direction: row;
        justify-content: flex-end;

        ${theme.breakpoints.only('lg')} {
            padding-top: 7px;
            padding-bottom: 7px;
        }
    `;

    const needDetailStyle = css`
        font-size: 12px;
        color: ${theme.colours.red[550]};
    `;

    const nameAndNeedAttendanceStyle = ({
        hasMinus = false,
        isAboveForm = false,
        isFullyRight = false,
    } = {}) => css`
        padding-right: ${hasMinus ? '16px' : '50px'};
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        flex: 1;

        ${theme.breakpoints.down('md')} {
            display: flex;
            margin-bottom: 24px;
            padding-right: 94px;
            transition: 300ms padding-right ease;

            ${isFullyRight && css` padding-right: 0;`};
        }

        ${isAboveForm && css`
            ${theme.breakpoints.up('lg')} {
                display: none;
            }
        `};
    `;

    const nameStyle = ({
        isSelected = false,
        isNew = false,
    } = {}) => css`
        color: ${isNew ? theme.colours.red[550] : theme.colours.gray3};

        ${isSelected ? css`
            color: ${isNew ? theme.colours.red[550] : theme.colours.curiousBlue2};
            font-weight: ${theme.fonts.weights.bold};
        ` : css`
            cursor: pointer;
        `};

        &:hover {
            font-weight: ${theme.fonts.weights.bold};
            color: ${isNew ? theme.colours.red[550] : theme.colours.curiousBlue2};
        }

        ${theme.breakpoints.down('md')} {
            justify-content: flex-start;
        }
    `;

    const specifiedFormStyle = ({
        isVisible = false,
        isExpanded = false,
        isExpandable = false,
        isExpanding = false,
        isLast = false,
    } = {}) => css`
        ${theme.breakpoints.up('lg')} {
            display: ${isVisible ? 'block' : 'none'};
        }

        ${theme.breakpoints.down('md')} {
            position: relative;
            padding: 16px 30px ${isExpandable ? 68 : 23}px;
            border-radius: 17px;
            background-color: ${theme.colours.grey[100]};
            overflow: ${isExpanded && !disableOverflow.current ? 'visible !important' : 'hidden'};

            ${!isExpanding && css` padding-bottom: 0; `};

            ${!isLast && css` margin-bottom: 16px; `};
        }

        .option-text, input {
            font-size: 14px;
            line-height: 17px;
        }
    `;

    const formActionsContainerStyle = css`
        display: flex;
        padding-top: 30px;
        justify-content: flex-end;

        ${theme.breakpoints.up('3xl')} {
            padding-top: 70px;
        }

        ${theme.breakpoints.only('xl')} {
            padding-top: 44px;
        }

        ${theme.breakpoints.down('md')} {
            padding-top: 10px;
            justify-content: space-between;
            align-items: flex-start;
        }
    `;

    const needEveryAttendeesDetailStyle = css`
        font-size: 12px;
        color: white;
    `;

    const exitModalStyle = css`
        ${theme.breakpoints.up('xl')} {
            width: 523px;
            max-width: 523px;
            height: 212px;

            padding: 26px 29px 23px;
        }

        ${theme.breakpoints.between('sm', 'lg')} {
            width: 431px;
            max-width: 431px;
            height: 202px;
            padding: 26px 17px 21px;
        }

        ${theme.breakpoints.down('xs')} {
            width: 314px;
            max-width: 314px;
            height: 195px;
            padding: 26px 10px 21px;
        }
    `;

    const dialogContentStyle = css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        color: ${theme.colours.shipGray};
    `;

    const areYouSureExitStyle = css`
        font-size: 18px;
        font-weight: ${theme.fonts.weights.bold};

        ${theme.breakpoints.down('lg')} {
            padding: 0 30px;
            font-size: 16px;
            line-height: 20px;
        }
    `;

    const unsavedChangesMessageStyle = css`
        padding: 24px 58px 35px;
        font-size: 16px;
        line-height: 19px;

        ${theme.breakpoints.between('sm', 'lg')} {
            padding: 22px 58px 22px;
        }

        ${theme.breakpoints.down('lg')} {
            font-size: 14px;
            line-height: 18px;
        }

        ${theme.breakpoints.down('xs')} {
            padding: 20px 20px;
            font-size: 14px;
            line-height: 18px;
        }
    `;

    const buttonGroupStyle = css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
    `;

    const buttonStyle = ({ isStayButton = false } = {}) => css`
        width: 224px;
        height: 50px;
        min-height: 50px;
        max-height: 50px;

        ${theme.breakpoints.between('sm', 'lg')} {
            width: 188px;
        }

        ${theme.breakpoints.down('xs')} {
            width: 144px;
            height: 38px;
            min-height: 38px;
            max-height: 38px;
        }

        > div {
            color: ${isStayButton ? theme.colours.shipGray : 'white'};
            background-color: ${isStayButton ? theme.colours.gold : theme.colours.red[550]};

            &:hover {
                background-color: ${isStayButton ? theme.colours.mySin : theme.colours.wildWatermelon2};
            }
        }
    `;

    const buttonTextStyle = ({ isStayButton = false } = {}) => css`
        font-size: 16px;
        font-weight: ${theme.fonts.weights.normal};
        color: ${isStayButton ? theme.colours.shipGray : 'white'};
        background-color: transparent;

        ${theme.breakpoints.down('lg')} {
            font-size: 14px;
        }
    `;

    const attendeeStyle = css`
        font-size: 22px;
        font-weight: ${theme.fonts.weights.bold};
        color: ${theme.colours.doveGray4};
    `;

    const chevronStyle = ({ isUp = false, hasError = false } = {}) => css`
        ${theme.breakpoints.up('lg')} {
            display: none;
        }

        position: absolute;
        right: 80px;
        bottom: 20px;
        width: 19px;
        height: 12px;
        fill: ${theme.colours.curiousBlue2};
        transform: rotateX(180deg);
        transition: transform 300ms ease;
        cursor: pointer;

        ${isUp && css`
            transform: rotateX(0);
        `};

        ${hasError && css` fill: ${theme.colours.red[550]}`};
    `;

    const errorIconSmallBpsStyle = css`
        ${theme.breakpoints.up('lg')} {
            display: none;
        }

        position: absolute;
        right: 56px;
        bottom: 8px;
        top: unset;
    `;

    return (
        <>
            {registrantIndexQueuedToRemove !== null && (
                <ConfirmationRemoveAlertDialog
                    message={'Are you sure you want to remove this attendee?'}
                    onClose={() => setRegistrantIndexQueuedToRemove(null)}
                    onLeftButtonClick={() => setRegistrantIndexQueuedToRemove(null)}
                    onRightButtonClick={() => removeRegistrant()}
                />
            )}
            <EventPageContentContainer>
                <div css={leftContainerStyle}>
                    <div css={attendeesContainerStyle}>
                        <div css={attendeesNumberStyle}>
                            <div css={attendeeStyle}>
                                Attendee
                            </div>
                            <div css={attendeeStyle}>
                                {totalNumberOfRegistrants}
                            </div>
                        </div>
                        {Object.entries(allRegistrantNames).map(([i, name]) => {
                            const index = parseInt(i);
            
                            return (
                                <div css={attendeeNameStyle} key={index}>
                                    <div
                                        css={nameAndNeedAttendanceStyle({
                                            hasMinus: index !== 0,
                                            isFullyRight: getIsIdExpanded(0, collapseIdPrefix),
                                        })}
                                    >
                                        <div
                                            css={nameStyle({
                                                isSelected: isUp(breakpoint, 'lg') ?
                                                    selectedRegistrantIndex === index :
                                                    getIsIdExpanded(index, collapseIdPrefix),
                                                isNew: !name,
                                            })}
                                            onClick={() => onClickName(index)}
                                        >
                                            {typeof name === 'string' ? name : 'New'}
                                        </div>
                                        {!name && (
                                            <div css={needDetailStyle}>
                                                Need attendance detail
                                            </div>
                                        )}
                                    </div>
                                    {index !== 0 && (
                                        <Minus
                                            css={plusAndMinusIconContainerStyle()}
                                            onClick={() => onClickRemoveRegistrant(index - 1)}
                                        />
                                    )}
                                </div>
                            )})}
                        <div css={addNewPersonStyle}>
                            <div css={inputTextStyle}>
                                Add new attendee
                            </div>
                            <Plus
                                css={plusAndMinusIconContainerStyle({ isPlus: true })}
                                onClick={incrementAdditionalRegistrants}
                            />
                        </div>
                    </div>
                </div>
                <EventFormContainer>
                    <ContentCombine fontSizeType={'25'} css={eventRegistrationStyle}>
                        Event <span css={registrationStyle}>Registration</span>
                    </ContentCombine>
                    <Form onSubmit={onSubmitRegistration}>
                        <FormComponent
                            css={specifiedFormStyle({
                                isVisible: selectedRegistrantIndex === 0,
                                isExpandable: allRegistrantNames.length > 1,
                                isExpanding: getIsIdExpanded(0, collapseIdPrefix),
                                isExpanded: getIsIdExpandedDelayed(0, collapseIdPrefix),
                                isLast: totalNumberOfRegistrants === 1,
                            })}
                            {...FormComponent === Collapse && {
                                isOpen: getIsIdExpanded(0, collapseIdPrefix),
                                collapseHeight: '56px',
                            }}
                        >
                            <div ref={ref => containerRefs.current[collapseIdPrefix + 0] = ref}>
                                <div
                                    css={nameAndNeedAttendanceStyle({
                                        isAboveForm: true,
                                        isFullyRight: getIsIdExpanded(0, collapseIdPrefix),
                                    })}
                                >
                                    <ContentCombine
                                        css={nameStyle({
                                            isSelected: isUp(breakpoint, 'lg') ?
                                                selectedRegistrantIndex === 0 :
                                                getIsIdExpanded(0, collapseIdPrefix),
                                            isNew: !allRegistrantNames[0],
                                        })}
                                        onClick={() => onClickName(0)}
                                        fontSizeType={'20'}
                                    >
                                        {allRegistrantNames[0] || 'New'}
                                    </ContentCombine>
                                    {!allRegistrantNames[0] && (
                                        <div css={needDetailStyle}>
                                            Need attendance detail
                                        </div>
                                    )}
                                </div>
                                <RegistrationForm
                                    formFields={formFieldsForRender}
                                    fields={fields}
                                    errors={errors}
                                    onFieldChange={(name, value, opArgs) => onChange(name, value, 0, opArgs)}
                                />
                            </div>
                            {Object.keys(allRegistrantNames).length > 1 && (
                                <>
                                    {registrantsWithErrorIndices.includes(0) && (
                                        <ErrorIcon css={errorIconSmallBpsStyle} />
                                    )}
                                    <Chevron
                                        onClick={() => toggleExpanded(0)}
                                        css={chevronStyle({
                                            isUp: getIsIdExpanded(0, collapseIdPrefix),
                                            hasError: registrantsWithErrorIndices.includes(0),
                                        })}
                                    />
                                </>
                            )}
                        </FormComponent>
                        {Object.keys(fields.additionalRegistrants).map((i: string) => {
                            const index = parseInt(i);

                            return (
                                <FormComponent
                                    key={index}
                                    css={specifiedFormStyle({
                                        isVisible: index + 1 === selectedRegistrantIndex,
                                        isExpandable: true,
                                        isExpanding: getIsIdExpanded(index + 1, collapseIdPrefix),
                                        isExpanded: getIsIdExpandedDelayed(index + 1, collapseIdPrefix),
                                        isLast: index === (Object.keys(fields.additionalRegistrants).length - 1),
                                    })}
                                    {...FormComponent === Collapse && {
                                        isOpen: getIsIdExpanded(index + 1, collapseIdPrefix),
                                        collapseHeight: '56px',
                                    }}
                                >
                                    <div
                                        ref={ref => containerRefs.current[collapseIdPrefix + (index + 1)] = ref}
                                        css={nameAndNeedAttendanceStyle({
                                            isAboveForm: true,
                                            isFullyRight: getIsIdExpanded(index + 1, collapseIdPrefix),
                                        })}
                                    >
                                        <ContentCombine
                                            css={nameStyle({
                                                isSelected: isUp(breakpoint, 'lg') ?
                                                    selectedRegistrantIndex === index + 1 :
                                                    getIsIdExpanded(index + 1, collapseIdPrefix),
                                                isNew: !allRegistrantNames[index + 1],
                                            })}
                                            fontSizeType={'20'}
                                            onClick={() => onClickName(index + 1)}
                                        >
                                            {allRegistrantNames[index + 1] || 'New'}
                                        </ContentCombine>
                                        {!allRegistrantNames[index + 1] && (
                                            <div css={needDetailStyle}>
                                                Need attendance detail
                                            </div>
                                        )}
                                    </div>
                                    <RegistrationForm
                                        key={index}
                                        fields={fields.additionalRegistrants?.[index]}
                                        errors={errors?.additionalRegistrants?.[index]}
                                        formFields={
                                            getFormFieldsForRender(
                                                additionalRegistrantFormFields,
                                                fields.additionalRegistrants?.[index],
                                                {
                                                    innerData: {
                                                        fieldId: 'additionalRegistrants',
                                                        index,
                                                    }
                                                }
                                            )
                                        }
                                        onFieldChange={(name, value, opArgs) => {
                                            onChange(name, value, index + 1, {
                                                ...opArgs,
                                                innerData: {
                                                    fieldId: 'additionalRegistrants',
                                                    index,
                                                }
                                            })
                                        }}
                                        registrantIndex={index}
                                        mandatoryCheckKey={'isMandatoryForSecondaryRegistrant'}
                                        {...isUp(breakpoint, 'lg') && {
                                            css: specifiedFormStyle({
                                                isVisible: index + 1 === selectedRegistrantIndex,
                                                isExpandable: true,
                                                isExpanding: getIsIdExpanded(index + 1, collapseIdPrefix),
                                                isExpanded: getIsIdExpandedDelayed(index + 1, collapseIdPrefix),
                                                isLast: index === (Object.keys(fields.additionalRegistrants).length - 1),
                                            })
                                        }}
                                    />
                                    {registrantsWithErrorIndices.includes(index + 1) && (
                                        <ErrorIcon css={errorIconSmallBpsStyle} />
                                    )}
                                    <Chevron
                                        onClick={() => toggleExpanded(index + 1)}
                                        css={chevronStyle({
                                            isUp: getIsIdExpanded(index + 1, collapseIdPrefix),
                                            hasError: registrantsWithErrorIndices.includes(index + 1),
                                        })}
                                    />
                                    <Minus
                                        css={plusAndMinusIconContainerStyle({ isForSmallBreakpoints: true })}
                                        onClick={() => onClickRemoveRegistrant(index)}
                                    />
                                </FormComponent>
                            )})}
                        {registrationPageData.footerText && (
                            <div dangerouslySetInnerHTML={{ __html: registrationPageData.footerText}} />
                        )}
                        <div css={formActionsContainerStyle}>
                            {isDown(breakpoint, 'md') && (
                                <div css={addNewPersonStyle}>
                                    <div css={inputTextStyle}>
                                        Add new attendee
                                    </div>
                                    <Plus
                                        css={plusAndMinusIconContainerStyle({ isPlus: true })}
                                        onClick={incrementAdditionalRegistrants}
                                    />
                                </div>
                            )}
                            <EventRegistrationFormActions isDisabled={!isValid}>
                                <FormButton type={"submit"} loading={isButtonLoading}>
                                    Next
                                </FormButton>
                                <div
                                    css={needEveryAttendeesDetailStyle}
                                    className={'need-detail'}
                                >
                                    Need every attendee's detail
                                </div>
                            </EventRegistrationFormActions>
                        </div>
                    </Form>
                </EventFormContainer>
            </EventPageContentContainer>
            <RouteLeavingGuard when={isRouteLeavingGuardActive}>
                {(onStay: () => void, onLeave: () => void) => (
                    <Dialog
                        extraStyle={exitModalStyle}
                        closeButtonVariant={'circle-overflowing'}
                        onClose={onStay}
                        closeOnOutsideClick
                    >
                        <DialogContent css={dialogContentStyle} center>
                            <div css={areYouSureExitStyle}>
                                Are you sure you want to leave this page?
                            </div>
                            <div css={unsavedChangesMessageStyle}>
                                You have unsaved changes that will be lost if you continue.
                            </div>
                            <div css={buttonGroupStyle}>
                                <RoundButton
                                    onButtonClick={onStay}
                                    css={buttonStyle({ isStayButton: true })}
                                    buttonTextCustomStyle={buttonTextStyle({ isStayButton: true })}
                                >
                                    Stay on this page
                                </RoundButton>
                                <RoundButton
                                    css={buttonStyle()}
                                    onButtonClick={onLeave}
                                    buttonTextCustomStyle={buttonTextStyle()}
                                >
                                    Leave this page
                                </RoundButton>
                            </div>
                        </DialogContent>
                    </Dialog>
                )}
            </RouteLeavingGuard>
        </>
    );
}

export default EventRegistrationPage;

